import {
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EyeIcon } from "lucide-react";
import { formatDate } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import type { ReceiptListItemDTO } from "../api/client";

type ReceiptsTableProps = {
  isLoading: boolean;
  data: ReceiptListItemDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (id: string) => void;
};

export function ReceiptsTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onPaginationChange,
  onSortingChange,
  onView,
}: ReceiptsTableProps) {
  const columns: TanstackReactTable.ColumnDef<ReceiptListItemDTO>[] = [
    createSortableColumn<ReceiptListItemDTO>("receiptNo", "ເລກທີ", {
      size: 140,
      cell: ({ row }) => (
        <span className="font-medium font-mono">{row.original.receiptNo}</span>
      ),
    }),
    createSortableColumn<ReceiptListItemDTO>("issuedAt", "ວັນທີອອກ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDate(row.original.issuedAt)}
        </span>
      ),
    }),
    createSortableColumn<ReceiptListItemDTO>("issuedByName", "ຜູ້ອອກ", {
      size: 160,
      cell: ({ row }) => <span>{row.original.issuedByName ?? "-"}</span>,
    }),
    createSortableColumn<ReceiptListItemDTO>("totalAmount", "ຈຳນວນເງິນ", {
      size: 140,
      cell: ({ row }) => {
        const r = row.original;
        const hasDiscount = r.discountAmount != null && r.discountAmount > 0;
        const originalAmount = r.originalAmount ?? r.totalAmount;
        return (
          <div className="flex flex-col items-end gap-0.5">
            {hasDiscount && originalAmount > r.totalAmount ? (
              <>
                <span className="text-muted-foreground text-xs line-through">
                  {originalAmount.toLocaleString("lo-LA")} ກີບ
                </span>
                <span className="text-muted-foreground text-xs">
                  -{r.discountAmount?.toLocaleString("lo-LA")} ກີບ
                  {r.discountType === "percentage" && r.discountValue != null
                    ? ` (${r.discountValue}%)`
                    : ""}
                </span>
              </>
            ) : null}
            <span className="font-semibold">
              {r.totalAmount.toLocaleString("lo-LA")} ກີບ
            </span>
          </div>
        );
      },
    }),
    {
      id: "actions",
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ເບິ່ງ",
              icon: <EyeIcon className="size-4" />,
              onClick: () => onView(row.original.id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <DataTable<ReceiptListItemDTO, unknown>
      noDataMessage="ບໍ່ພົບໃບເກັບເງິນ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (!sorting || sorting.length === 0) return;
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id ?? "", !!sorting[0]?.desc);
      }}
    />
  );
}
